import * as React from 'react';
import './itemKeepActiveModal.scss';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Metadata } from '../../types';
import { CPPElements } from 'rcd-cpp-docs';
import { getToken } from '../../utils/authClient';
import { useSelector } from '../../utils/useSelector';
import { useActions } from '../View/viewActions';
import { useActions as useMetadataFormActions } from '../MetadataForm/metadataFormActions';
import { formatDate, getEmployeeName, getSchema, parseDate } from '../../utils';
import { Maintenance } from '../Icon/icon';
import { NodeType } from '../../enums/NodeType';

export interface ExpiryRequestCallbackParams {
  itemId: number;
  expiryRequestByName: string;
  expiryRequestByRefId: string;
  expiryRequestJustification?: string;
  expiryRequestType?: string;
  expiryDate?: string;
}

type Props = {
  onCancel: () => any;
  data: Partial<Metadata>;
};

export const ItemKeepActiveModal = ({ onCancel, data }: Props) => {
  const itemFormSchema = useSelector(s => s.form?.schema);
  const coeDisplayField = useSelector(
    s =>
      getSchema(
        s.planning?.selectedWorkspace?.schemas,
        NodeType.MaintenanceCOEProposal
      )?.props?.displayField
  );

  const { updateExpiryView } = useActions();
  const { updateFields } = useMetadataFormActions();
  const cppKeepActive = React.useRef<CPPElements.ItemKeepActive>(null);
  const onDone = (params: ExpiryRequestCallbackParams) => {
    onCancel();
    updateExpiryView(params);
    if (itemFormSchema) {
      if (!params?.expiryRequestType) {
        const fields = itemFormSchema?.props?.fields;
        const expiryDateField = fields?.filter(f => f?.key == 'Item.expiryDate')
          ?.length
          ? fields?.filter(f => f?.key == 'Item.expiryDate')[0]
          : null;
        const expiryDateByMonthField = fields?.filter(
          f => f?.key == 'Item.expiryDateByMonth'
        )?.length
          ? fields?.filter(f => f?.key == 'Item.expiryDateByMonth')[0]
          : null;
        const expiryDateByQuarterField = fields?.filter(
          f => f?.key == 'Item.expiryDateByQuarter'
        )?.length
          ? fields?.filter(f => f?.key == 'Item.expiryDateByQuarter')[0]
          : null;
        const updatedExpiryDate = params.expiryDate;
        const parsedExpiryDate = parseDate(params.expiryDate);
        const formattedExpiryDate = formatDate(parsedExpiryDate, true);

        const updatedDateByMonth = formattedExpiryDate
          ?.split('-')
          ?.map((val: any, index) => {
            if (index < 2) {
              return val;
            } else {
              return null;
            }
          })
          .filter(x => x)
          .join('-');
        const updatedDateByQuarter = formattedExpiryDate
          ?.split('-')
          ?.map((val: any, index) => {
            if (index == 0) {
              return val;
            } else if (index == 1) {
              return getQuarter(parsedExpiryDate);
            } else {
              return null;
            }
          })
          .filter(x => x)
          .join('-');

        updateFields([
          {
            field: expiryDateField,
            value: updatedExpiryDate
          },
          { field: expiryDateByMonthField, value: updatedDateByMonth },
          { field: expiryDateByQuarterField, value: updatedDateByQuarter }
        ]);
      }
    }
  };
  React.useEffect(() => {
    if (cppKeepActive.current) {
      cppKeepActive.current.getToken = getToken;
      cppKeepActive.current.onDone = onDone as any;
      cppKeepActive.current.onCancel = onCancel;
      (cppKeepActive.current as any).getAdditionalInfo = getAdditionalInfo(
        coeDisplayField,
        data
      );
    }
  }, []);

  return (
    <Modal
      header="Keep Active"
      id="itemKeepActiveModal"
      onClose={onCancel}
      height="650px"
      width="1100px"
    >
      <cpp-item-keep-active
        ref={cppKeepActive}
        id="cppKeepActive"
        itemId={data.props.itemId}
      ></cpp-item-keep-active>
    </Modal>
  );
};

const getQuarter = date => {
  const dateObj = new Date(date);
  return `Q${Math.floor((dateObj.getMonth() + 3) / 3)}`;
};

export const getAdditionalInfo =
  (coeDisplayField: string, data: Partial<Metadata>) => () => {
    if (!coeDisplayField) return null;
    if (!data?.props) return null;
    const modifiedBy =
      getEmployeeName(data.props.maintenanceCOEModifiedBy, {
        withAuditDate: true
      }) || 'Unknown';
    return data.props.maintenanceCOERecommendation
      ?.map(r => {
        if (r?.props && r.props[coeDisplayField]) {
          return {
            header: (
              <>
                <Maintenance />
                {r.props[coeDisplayField]} requested by Maintenance COE:{' '}
                {modifiedBy}
              </>
            ),
            description: data.props.maintenanceCOENote
          };
        }
      })
      ?.filter(x => x);
  };
